import React, { ReactElement, useContext, useEffect, useState } from 'react'

import { Context } from '@/components/Context'
import { connectWalletKitWithHandlers, hasSessionForToken, WalletKitInstance } from '@/controllers/walletKitController'
import { PersonalWallet } from '@/database/personalwallet'
import Box from '@/components/boxes/Box'
import { shortenAddress } from '@/utils/shortenAddress'

const getExpirySeconds = (wcUri: string): number | null => {
    const q = wcUri.split('?', 2)[1]
    if (!q) return null
    const expiry = new URLSearchParams(q).get('expiryTimestamp')
    return expiry ? Number(expiry) : null
}

const ConnectWithNft = ({ tokenId, wallet }: { tokenId: string; wallet: PersonalWallet }): ReactElement => {
    const { walletProvider } = useContext(Context)
    const [walletKit, setWalletKit] = useState<WalletKitInstance | null>(null)
    const [connected, setConnected] = useState(false)
    const [uri, setUri] = useState('')

    const handleConnect = async () => {
        if (!walletProvider) return

        // initialize kit if we do not have one yet
        const kit =
            walletKit ??
            (await connectWalletKitWithHandlers({
                tokenId,
                walletProvider,
                onConnected: () => setConnected(true),
                onDisconnected: () => setConnected(false),
            }))

        setWalletKit(kit)

        // restore existing session (fast path)
        if (await hasSessionForToken(kit, tokenId, walletProvider)) {
            setConnected(true)
        }

        // perform pairing if a URI is available and the wallet is not yet paired
        if (!connected && uri) {
            await kit.pair({ uri })
        }
    }

    useEffect(() => {
        const interval = setInterval(async () => {
            if (!document.hasFocus()) return
            const nowSec = Math.floor(Date.now() / 1000)
            const clipExpiry = getExpirySeconds(uri)
            if (clipExpiry && clipExpiry < nowSec) {
                setUri('')
            }
            try {
                const clipText = await navigator.clipboard.readText()
                const clipTextExpiry = getExpirySeconds(clipText)
                const isExpired = clipTextExpiry && clipTextExpiry < nowSec
                if (!isExpired && clipText.startsWith('wc:') && clipText.includes('@2') && !uri.includes(clipText)) {
                    setUri(clipText)
                }
            } catch (error) {
                console.error('Error reading clipboard:', error)
            }
        }, 2000)
        return () => clearInterval(interval)
    }, [uri])

    return (
        <div className="p-4">
            {!connected && (
                <>
                    <div>
                        <h2 className="text-xl font-bold mb-2">Connect With Your Personal Wallet</h2>
                        <input
                            type="text"
                            value={uri}
                            onChange={(e) => setUri(e.target.value)}
                            placeholder="Wallet Connect URI"
                            className="input"
                        />
                    </div>
                    <button onClick={handleConnect}>Connect</button>
                </>
            )}
            {connected && (
                <div className="mt-4 text-green-600 header-container">
                    <span className="header-container gradient-text large-bold">
                        <Box
                            caption={`Wallet ${shortenAddress(wallet?.address)} is connected to a dApp`}
                            text=""
                            styleCaption="gradient-text bold large-bold"
                        />
                    </span>
                </div>
            )}
        </div>
    )
}

export default ConnectWithNft
